import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";

import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";

import "./index.css";

import "@/i18n/i18n";

import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { QueryClient, QueryClientProvider } from "react-query";
import queryClientConfig from "project-config/services/request/query-client-config";

import ErrorBoundary from "@/components/ErrorBoundary";
import Loading from "@/components/Loading";
import GlobalStoreProvider from "./store/GlobalStore";
import { SubAccountStoreProvider } from "@/store/SubAccountStore";
import { PermissionStoreProvider } from "@/store/PermissionStore";
import { isEn } from "project-config/utils/env";
import ThemeManager from "project-config/services/theme-manager";
import {
  StyleProvider,
  legacyLogicalPropertiesTransformer,
} from "@ant-design/cssinjs";
import { ToastStoreProvider } from "./store/toast-store";
import { shouldHideAIChat } from "project-config/utils";

const LazyAIChatHost = lazy(() =>
  import("./components/ai-chat").then(({ AIChatHost }) => ({
    default: AIChatHost,
  }))
);

ThemeManager.init();

export const queryClient = new QueryClient(queryClientConfig);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// 暂定兼容到88版本，客户端内嵌浏览器版本为87
const currentChromeVersion = navigator.userAgent
  .toLowerCase()
  .match(/chrome\/([\d.]+)/);
const lowerBroswerVersion = currentChromeVersion
  ? +currentChromeVersion[1].split(".")[0] < 88
  : !!currentChromeVersion;

function main() {
  const shouldLoadAIChat = !shouldHideAIChat();

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          locale={isEn() ? enUS : zhCN}
          autoInsertSpaceInButton={false}
          theme={{
            token: {
              colorText: "#FFFFFF",
              borderRadius: 4,
              lineHeight: 1.5715,
            },
          }}
        >
          <GlobalStoreProvider>
            <ErrorBoundary>
              <Suspense fallback={<Loading />}>
                <SubAccountStoreProvider>
                  <PermissionStoreProvider>
                    <ToastStoreProvider>
                      <StyleProvider
                        hashPriority={lowerBroswerVersion ? "high" : undefined}
                        transformers={[legacyLogicalPropertiesTransformer]}
                      >
                        <>
                          <App />
                          {shouldLoadAIChat && (
                            <Suspense fallback={null}>
                              <LazyAIChatHost />
                            </Suspense>
                          )}
                        </>
                      </StyleProvider>
                    </ToastStoreProvider>
                  </PermissionStoreProvider>
                </SubAccountStoreProvider>
              </Suspense>
            </ErrorBoundary>
          </GlobalStoreProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

if (
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_ENABLE_MOCK === "true"
) {
  /* webpackIgnore: true */
  // @ts-ignore
  import("../../../dev/mock").then(({ enableMocking }) => {
    enableMocking().then(() => {
      main();
    });
  });
} else {
  main();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
